/**
 * Trip CO₂ Calculator — per-segment math.
 *
 * Pure functions that turn a backend `EmissionsResult` (one segment,
 * one-way) into a chart-ready `ResultRow`. Two responsibilities:
 *
 * 1. **Breakdown** — split the comprehensive kg into electricity, fuel,
 *    and radiative-forcing components for the stacked-bar chart
 *    (PRD §5.5).
 * 2. **Round-trip doubling** — the backend is stateless about RT
 *    semantics; we double client-side (PRD §5.3).
 *
 * Multi-row composition (the default-vs-custom rules, the synthetic
 * Plane+Train mix row, sorting) lives in `buildComparison.ts`. This
 * module only knows about one segment at a time.
 *
 * Phase 2 of the Trip CO₂ Calculator (see PRD §11).
 */

import type { EmissionsResult, TransportMode } from '../admin/carbonAccounting/api';
import type { ConfigByMode } from './modes';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Decomposition of total kg CO₂e by emission source. The chart
 * stacks these in the order electric → fuel → rfi (PRD §5.5).
 *
 * For non-flight modes, exactly one of {electric, fuel} is non-zero
 * and rfi is always 0 — the chart renders these as a single-color
 * bar matching the dominant source.
 */
export interface EmissionBreakdown {
  /** kg from grid electricity (electric trains, EVs). */
  electric: number;
  /** kg from fossil-fuel combustion (everything else, plus the CO₂ portion of flights). */
  fuel: number;
  /** kg-equivalent from non-CO₂ high-altitude effects. Aviation only. */
  rfi: number;
}

/**
 * Chart-ready row for a single transport segment. The kg and
 * distance are *as displayed* — i.e. doubled if `roundTrip` is true.
 *
 * The generic param lets call sites narrow `config` when they know
 * the mode statically; most consumers will treat rows uniformly as
 * `ResultRow`.
 */
export interface ResultRow<M extends TransportMode = TransportMode> {
  mode: M;
  /** Snapshot of the mode-config that produced this row (for chart labels). */
  config: ConfigByMode[M];
  /** Total kg CO₂e for the trip (sum of `breakdown`). */
  kg: number;
  breakdown: EmissionBreakdown;
  /** Detour-adjusted distance, in km, for the trip. */
  distanceKm: number;
  /** True iff this row corresponds to the user's selected (mode, config). */
  isChosen: boolean;
  /** True iff `kg`, `distanceKm`, and `breakdown` reflect a doubled round-trip. */
  roundTrip: boolean;
  /**
   * Prepared, human-readable explanation of how this row's kg was
   * computed — straight from the backend's comprehensive engine
   * (`backend/services/carbonAccounting/explanations.js`). Surfaced
   * verbatim in the chart's methodology panel.
   */
  explanation?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * True if the given (mode, config) draws its energy from the grid
 * rather than combustion. For v1: train-electric and car-electric
 * only. Hybrid cars are treated as fuel — the DEFRA hybrid factor
 * (0.130 kg/vkm) sits much closer to petrol (0.211) than to grid EV
 * (0.082), so attributing hybrid kg to "electricity" would mislead.
 */
function isGridPowered<M extends TransportMode>(
  mode: M,
  config: ConfigByMode[M]
): boolean {
  if (mode === 'train') {
    return (config as ConfigByMode['train']).type === 'electric';
  }
  if (mode === 'car') {
    return (config as ConfigByMode['car']).fuel === 'electric';
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Splits a total kg into electric / fuel / rfi shares.
 *
 * - **Aviation**: the `comprehensive` kg already includes the RF
 *   multiplier, so `fuel = total / rf` and `rfi = total − fuel`.
 *   When `rfMultiplier` is missing or ≤1 (defensive), we treat the
 *   whole row as fuel.
 * - **Grid-powered ground**: train-electric or car-electric → all
 *   electric.
 * - **Everything else**: all fuel.
 *
 * Pure: deterministic, no side effects. `totalKg = 0` returns zeros
 * (avoids NaN propagating through the chart).
 */
export function deriveBreakdown<M extends TransportMode>(args: {
  mode: M;
  config: ConfigByMode[M];
  totalKg: number;
  /** Aviation only; absent or ≤1 means no RF uplift was applied. */
  rfMultiplier?: number;
}): EmissionBreakdown {
  if (args.totalKg <= 0) {
    return { electric: 0, fuel: 0, rfi: 0 };
  }

  if (args.mode === 'airplane') {
    const rf = args.rfMultiplier;
    if (!rf || rf <= 1) {
      return { electric: 0, fuel: args.totalKg, rfi: 0 };
    }
    const fuel = args.totalKg / rf;
    const rfi = args.totalKg - fuel;
    return { electric: 0, fuel, rfi };
  }

  if (isGridPowered(args.mode, args.config)) {
    return { electric: args.totalKg, fuel: 0, rfi: 0 };
  }

  return { electric: 0, fuel: args.totalKg, rfi: 0 };
}

/**
 * Normalises a backend `EmissionsResult` into a chart row. The row
 * is one-way; call `withRoundTrip` to double it.
 *
 * Uses the *comprehensive* numbers — that's the realistic estimate
 * the chart shows (detour-adjusted distance, RF-included for
 * flights). The simplified numbers are only used by the methodology
 * panel.
 */
export function toResultRow<M extends TransportMode>(input: {
  mode: M;
  config: ConfigByMode[M];
  result: EmissionsResult;
  isChosen: boolean;
}): ResultRow<M> {
  const { comprehensive } = input.result;
  return {
    mode: input.mode,
    config: input.config,
    kg: comprehensive.co2eKg,
    distanceKm: comprehensive.distanceKm,
    breakdown: deriveBreakdown({
      mode: input.mode,
      config: input.config,
      totalKg: comprehensive.co2eKg,
      rfMultiplier: comprehensive.rfMultiplier,
    }),
    isChosen: input.isChosen,
    roundTrip: false,
    explanation: comprehensive.explanation,
  };
}

/**
 * Doubles a one-way row into a round-trip row. PRD §5.3: round-trip
 * math is doubled client-side — the backend never sees `roundTrip`,
 * so the same one-way segment can serve as both a one-way result
 * and (after doubling) a round-trip result.
 *
 * Idempotency: this function does *not* check `row.roundTrip`. Callers
 * are expected to feed it one-way rows. Doubling a doubled row is a
 * bug — the test suite pins this so a future regression surfaces.
 */
export function withRoundTrip<M extends TransportMode>(row: ResultRow<M>): ResultRow<M> {
  return {
    ...row,
    kg: row.kg * 2,
    distanceKm: row.distanceKm * 2,
    breakdown: {
      electric: row.breakdown.electric * 2,
      fuel: row.breakdown.fuel * 2,
      rfi: row.breakdown.rfi * 2,
    },
    roundTrip: true,
  };
}
